import { Box, CircularProgress, Menu, MenuItem, SvgIconProps } from '@mui/material';
import { useState, MouseEvent, ElementType } from 'react';
import { StyledButton } from './styles';
import { useTheme } from '@mui/material/styles';
import { DownloadUsage } from '@/graphql/types/downloads';
import { BootstrapTooltip } from '@/components/atoms/tooltip/bootstrap';

export interface MenuButtonOption {
  icon: ElementType<SvgIconProps>;
  label: string;
  onClick: () => void;
  isLimited?: boolean;
}

interface MenuButtonProps {
  label?: string;
  icon?: ElementType<SvgIconProps>;
  startIcon?: ElementType<SvgIconProps>;
  loading?: boolean;
  options: MenuButtonOption[];
  variant?: 'contained' | 'outlined' | 'text';
  isHeader?: boolean;
  downloadUsage?: DownloadUsage;
  limitReachedMessage?: string;
}

export const MenuButton = ({
  label,
  icon: Icon,
  startIcon: StartIcon,
  loading,
  options,
  variant = 'contained',
  isHeader = false,
  downloadUsage,
  limitReachedMessage,
}: MenuButtonProps) => {
  const theme = useTheme();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const isLimitReached = downloadUsage ? downloadUsage.used >= downloadUsage.limit : false;

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const button = (
    <StyledButton
      id="menu-button"
      aria-controls={open ? 'menu-button' : undefined}
      aria-haspopup="true"
      aria-expanded={open ? 'true' : undefined}
      onClick={handleClick}
      startIcon={StartIcon && <StartIcon color="inherit" />}
      variant={variant}
      isHeader={isHeader}
    >
      {Icon && <Icon color="inherit" />}
      {loading ? (
        <Box sx={{ width: '63%' }}>
          <CircularProgress size={18} sx={{ color: theme.palette.action.active }} />
        </Box>
      ) : (
        label
      )}
    </StyledButton>
  );

  const getMenuItemLabel = (optionLabel: string, isLimited?: boolean) => {
    if (isLimited && downloadUsage) {
      return `${optionLabel} (${downloadUsage.used}/${downloadUsage.limit})`;
    }
    return optionLabel;
  };

  const isOptionDisabled = (isLimited?: boolean) => {
    return isLimited && isLimitReached;
  };

  return (
    <>
      {button}

      <Menu id="menu-button" anchorEl={anchorEl} open={open} onClose={handleClose}>
        {options.map(({ label: optionLabel, icon: Icon, onClick, isLimited }, index) => {
          const menuItem = (
            <MenuItem
              key={index}
              onClick={isOptionDisabled(isLimited) ? undefined : onClick}
              disabled={isOptionDisabled(isLimited)}
              sx={{
                gap: theme.spacing(1),
                color: theme.palette.text.primary,
                fontWeight: 600,
              }}
            >
              <Icon color="action" />
              {getMenuItemLabel(optionLabel, isLimited)}
            </MenuItem>
          );

          if (isOptionDisabled(isLimited) && limitReachedMessage) {
            return (
              <BootstrapTooltip key={index} title={limitReachedMessage}>
                <span>{menuItem}</span>
              </BootstrapTooltip>
            );
          }

          return menuItem;
        })}
      </Menu>
    </>
  );
};
